
import { useState, useEffect } from 'react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // SEO設定
    document.title = 'お問い合わせ | アットコール | キッチン・ユニットバス・水漏れ修理';
    
    // メタタグの設定
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'リフォームのご相談・お見積りはお気軽にお問い合わせください。埼玉県川口市の住宅リフォーム専門会社が丁寧にご対応いたします。無料見積もり実施中。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'お問い合わせ, リフォーム相談, 見積もり, 川口市, 住宅リフォーム');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`);
    }

    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "お問い合わせ",
      "description": "リフォームのご相談・お見積りはお気軽にお問い合わせください。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "有限会社アットコール",
        "telephone": "090-9397-4969",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "芝富士1-22-31",
          "addressLocality": "川口市",
          "addressRegion": "埼玉県",
          "postalCode": "333-0854",
          "addressCountry": "JP"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 35.8071,
          "longitude": 139.7243
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 必須項目のバリデーション
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      alert('必須項目を入力してください。');
      return;
    }

    // メールアドレスの簡単なバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('正しいメールアドレスを入力してください。');
      return;
    }

    // お問い合わせ内容の文字数制限
    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d41khmsqnjlrn9cd7nt0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-blue-600" style={{ fontFamily: '"Pacifico", serif' }}>
                有限会社アットコール
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                ホーム
              </a>
              <a href="/services" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                サービス
              </a>
              <a href="/about" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">
                会社概要
              </a>
              <a href="/contact" className="text-blue-600 font-medium cursor-pointer">
                お問い合わせ
              </a>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* ページタイトル */}
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせ</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              リフォームに関するご相談やお見積りのご依頼など、お気軽にお問い合わせください。
              専門スタッフが丁寧にご対応いたします。
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* お問い合わせフォーム */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">お問い合わせフォーム</h3>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  お問い合わせありがとうございます。内容を確認のうえ、2営業日以内にご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  送信に失敗しました。しばらく時間をおいて再度お試しください。
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                <div className="space-y-6">
                  {/* お名前 */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="山田太郎"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  {/* 電話番号 */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="03-1234-5678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {/* メールアドレス */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  {/* お問い合わせ内容 */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      お問い合わせ内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      maxLength={500}
                      placeholder="リフォームに関するご相談やご質問をお聞かせください。"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                      required
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.message.length}/500文字
                    </p>
                  </div>

                  {/* 送信ボタン */}
                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* 会社情報・連絡先 */}
            <div>
              <div className="space-y-8">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">会社情報</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">所在地</p>
                        <p className="text-gray-600">〒333-0854 埼玉県川口市芝富士1-22-31</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-phone-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">電話番号</p>
                        <p className="text-gray-600">090-9397-4969</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-blue-600"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">メールアドレス</p>
                        <p className="text-gray-600">info@atcalls.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-4">お急ぎの方へ</h3>
                  <p className="text-blue-800 mb-4">
                    緊急のご相談やお急ぎの場合は、お電話でのお問い合わせが最も迅速です。
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-fill text-blue-600 text-xl"></i>
                      <a href="tel:0120-8656-24" className="text-2xl font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer">
                        0120-8656-24
                      </a>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-phone-fill text-blue-600 text-xl"></i>
                      <a href="tel:090-9397-4969" className="text-2xl font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer">
                        090-9397-4969
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">対応エリア</h3>
                  <div className="grid grid-cols-1 gap-2">
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-600"></i>
                      <span className="text-gray-700">東京都</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-600"></i>
                      <span className="text-gray-700">埼玉県</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-600"></i>
                      <span className="text-gray-700">千葉県</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-600"></i>
                      <span className="text-gray-700">神奈川県</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-600"></i>
                      <span className="text-gray-700">その他　お問い合わせください。</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
